import { Box, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import {
  DataGridPremium,
  DataGridPremiumProps,
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { Issue } from "generated-graphql/graphql";
import { useCtrlWheelHorizontalScroll } from "hooks/useCtrlWheelHorizontalScroll";
import { Ref, useCallback, useEffect, useMemo, useRef } from "react";
import { DocumentRecommendationRow } from "routes/Customer/Documents/DocumentsGrid";
import { useDeepCompareMemoize } from "use-deep-compare-effect";

import { DataGridCell } from "./DataGridCell";
import { LinkContent } from "./LinkContent";

export type CustomGridColDef<Row extends GridValidRowModel = Record<string, any>> =
  GridColDef<Row> & {
    topAligned?: boolean;
    /**
     * Whether to display cell content wrapped in a link to the getRowLink href.
     * Behavior is automatic if getRowLink is provided. Set this to false if you want a column to explicitly NOT render as a link.
     */
    linkable?: boolean;
  };

export type DataGridProps<Row extends GridValidRowModel> = React.ComponentProps<
  typeof DataGrid<Row>
>;

// Custom loading overlay component for DataGrid
const DataGridLoadingOverlay = () => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="absolute"
      top="var(--DataGrid-headersTotalHeight)"
      left={0}
      right={0}
      bottom="var(--DataGrid-hasScrollX, 0px)"
      bgcolor={theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.5)" : "rgba(255, 255, 255, 0.5)"}
      zIndex={1000}
    >
      <CircularProgress />
    </Box>
  );
};

// Light wrapper of MUI's DataGridPremium component where we can specify global styles that should apply to all tables
// any default props can and should be overridable.
export function DataGrid<Row extends GridValidRowModel>({
  ref,
  noRowsMessage,
  columns: columnsInput,
  getRowLink,
  onPaginationModelChange,
  rowCount,
  ...props
}: Omit<DataGridPremiumProps<Row>, "columns"> & {
  ref?: Ref<HTMLDivElement | null>;
  noRowsMessage?: string;
  columns: CustomGridColDef<Row>[];
  getRowLink?: (row: Row) => string | null;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      rootRef.current = el;
      if (typeof ref === "function") {
        ref(el);
      }
    },
    [ref]
  );

  const attachHorizontalWheelScroll = useCtrlWheelHorizontalScroll();
  useEffect(() => {
    const scroller =
      rootRef.current?.querySelector<HTMLElement>(".MuiDataGrid-virtualScroller") ?? null;
    attachHorizontalWheelScroll(scroller);
    return () => attachHorizontalWheelScroll(null);
  }, [attachHorizontalWheelScroll]);

  const rowMinimumHeight = 38;

  const columnsInputStable = useDeepCompareMemoize(columnsInput);

  const columns = useMemo(() => {
    return columnsInputStable.map((column) => {
      const originalRender = column.renderCell;
      const isActionsColumn = column.field === "actions" || column.type === "actions";
      const explicitlyNonLinkable = column.linkable === false;
      const shouldLink = !isActionsColumn && !explicitlyNonLinkable && !!getRowLink;
      const shouldTopAlign = column.topAligned;

      if (!shouldLink && !shouldTopAlign) {
        return column;
      }

      return {
        ...column,
        // TODO find the correct type
        renderCell: (params: GridRenderCellParams<Row>) => {
          const row = params.row;
          const url = shouldLink ? getRowLink(row) : null;

          const value =
            originalRender?.(params) ??
            params.formattedValue ??
            row[column.field as keyof typeof row];

          let cellContent = value;

          if (url) {
            cellContent = (
              <LinkContent url={url} minHeight={rowMinimumHeight}>
                {cellContent}
              </LinkContent>
            );
          }

          if (shouldTopAlign) {
            return <DataGridCell>{cellContent}</DataGridCell>;
          }

          return cellContent;
        },
      };
    });
  }, [columnsInputStable, getRowLink]);

  const hasTopAlignedColumn = useMemo(
    () => columnsInputStable.some((col) => col.topAligned),
    [columnsInputStable]
  );

  const currentRowCount = rowCount ?? props.rows?.length ?? 0;

  const pageSizeOptions = useMemo(
    () => props.pageSizeOptions ?? [10, 25, 50, 100, 200, 300, 400, 500],
    [props.pageSizeOptions]
  );
  // Extract numeric values since pageSizeOptions can be numbers or {value, label} objects
  const pageSizes = useMemo(
    () => pageSizeOptions.map((option) => (typeof option === "number" ? option : option.value)),
    [pageSizeOptions]
  );

  const smallestOptionPageSize = pageSizes.length > 0 ? Math.min(...pageSizes) : 10;

  // In controlled mode, also consider the current pageSize in case it's not in pageSizeOptions
  const controlledPageSize = props.paginationModel?.pageSize;
  const smallestPageSize = controlledPageSize
    ? Math.min(smallestOptionPageSize, controlledPageSize)
    : smallestOptionPageSize;

  const defaultHideFooter = currentRowCount <= smallestPageSize;

  // TODO: find the right type
  const sxVal = useMemo(
    () => ({
      "& .MuiDataGrid-virtualScroller": {
        minHeight: "40px",
      },
      "& .MuiDataGrid-row:hover": {
        cursor: "pointer",
      },
      "& .MuiDataGrid-pinnedColumnHeaders": {
        boxShadow: "none",
      },
      "& .MuiDataGrid-pinnedColumns": {
        boxShadow: "none",
      },
      "& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within":
        {
          outline: "none",
        },
      "& .MuiDataGrid-cell": {
        minHeight: `${rowMinimumHeight}px`,
        display: "flex",
        alignItems: "center",
      },
      "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell:not(.actions)": {
        py: hasTopAlignedColumn ? "10px" : "inherit",
      },
      "& .wrapHeader": {
        lineHeight: "1rem",
      },
      "& .wrapHeader .MuiDataGrid-columnHeaderTitleContainer": {
        whiteSpace: "normal",
      },
      "& .wrapHeader .MuiDataGrid-colCellTitle, .wrapHeader .MuiDataGrid-columnHeaderTitle": {
        whiteSpace: "normal",
      },
      // Style for action cells
      "& .MuiDataGrid-row .MuiDataGrid-cell[data-field='actions'] div[role='menu']": {
        opacity: 0,
        transition: "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)",
      },
      // Show actions when hovering anywhere on the row
      "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='actions'] div[role='menu']": {
        opacity: 1,
      },
      ...props.sx,
    }),
    [hasTopAlignedColumn, props.sx]
  );
  const slotVal = useMemo(
    () => ({
      loadingOverlay: DataGridLoadingOverlay,
      ...(noRowsMessage
        ? {
            // MUI X v8 uses camelCase slot keys. `noRowsOverlay` fires for an
            // actually-empty dataset; `noResultsOverlay` fires when a filter
            // narrows results to zero. Override both so `noRowsMessage` shows
            // up in either case.
            noRowsOverlay: () => (
              <div style={{ padding: "2rem", textAlign: "center" }}>{noRowsMessage}</div>
            ),
            noResultsOverlay: () => (
              <div style={{ padding: "2rem", textAlign: "center" }}>{noRowsMessage}</div>
            ),
          }
        : undefined),
      ...props.slots,
    }),
    [noRowsMessage, props.slots]
  );

  const slotPropsVal = useMemo(
    () => ({
      ...props.slotProps,
    }),
    [props.slotProps]
  );

  return (
    <DataGridPremium
      ref={setRef}
      // Autosizer on mount if autosizeOptions are provided
      autosizeOnMount={!!props.autosizeOptions}
      rowHeight={38}
      disableColumnFilter={true}
      disablePivoting={true}
      disableRowGrouping={true}
      disableAggregation={true}
      onPaginationModelChange={onPaginationModelChange}
      slots={slotVal}
      slotProps={slotPropsVal}
      rowCount={props.paginationMode !== "server" ? undefined : currentRowCount}
      {...props}
      // Only set checkboxSelectionVisibleOnly when checkbox selection is actually enabled
      // This prevents MUI warnings when pagination is disabled but checkbox selection isn't used
      checkboxSelectionVisibleOnly={
        props.checkboxSelection ? (props.checkboxSelectionVisibleOnly ?? true) : undefined
      }
      hideFooterPagination={props.hideFooterPagination ?? defaultHideFooter}
      hideFooter={props.hideFooter ?? defaultHideFooter}
      columnHeaderHeight={props.columnHeaderHeight ?? 56} // this is the default; don't ask me
      pageSizeOptions={pageSizeOptions}
      columns={columns}
      sx={sxVal}
    />
  );
}

/**
 * Component to render a cell with a primary and secondary (smaller) text value
 */
export const CellWithSecondaryText = ({
  primaryValue,
  secondaryValue,
}: {
  primaryValue: string | null | undefined;
  secondaryValue: string | null | undefined;
}) => {
  const theme = useTheme();
  return (
    <Stack direction="column" width="100%" sx={{ py: theme.spacing(1) }}>
      <Typography variant="body2" noWrap>
        {primaryValue}
      </Typography>
      <Typography variant="caption" noWrap sx={{ fontSize: "10px" }}>
        {secondaryValue}
      </Typography>
    </Stack>
  );
};

export const IssueMessageCell = ({ issue }: { issue: Issue }) => {
  const theme = useTheme();
  return (
    <Typography
      sx={{
        color: theme.palette.error.main,
        whiteSpace: "normal",
        wordWrap: "break-word",
      }}
      variant="body2"
    >
      {issue.message}
    </Typography>
  );
};

export const RecommendationMessageCell = ({
  recommendation,
}: {
  recommendation: DocumentRecommendationRow;
}) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      width="100%"
      justifyContent="space-between"
      alignItems="center"
      spacing={theme.spacing(4)}
    >
      <Typography
        sx={{
          whiteSpace: "normal",
          wordWrap: "break-word",
        }}
        variant="body2"
      >
        {recommendation.message}
      </Typography>
      <Typography variant="caption">Recommended</Typography>
    </Stack>
  );
};
